import React from 'react';
import { Text, View } from '@react-pdf/renderer';
import type { Style } from '@react-pdf/types';
import { marked } from 'marked';

import { getMarkedDisplayOptions } from '../../../../lib/marked-display-options';
import { colors } from '../../../../lib/colors';

type TextStyle = React.ComponentProps<typeof Text>['style'];
type PdfTextStyle = Style;

/** Marked re-encodes apostrophes in token.text; decode before rendering. */
export function decodeHtmlEntities(text: string): string {
  if (!text) {
    return text;
  }

  let decoded = text;
  let previous = '';

  while (decoded !== previous) {
    previous = decoded;
    decoded = decoded
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&#(\d+);/g, (_, code: string) =>
        String.fromCharCode(parseInt(code, 10)),
      )
      .replace(/&#x([0-9a-f]+);/gi, (_, code: string) =>
        String.fromCharCode(parseInt(code, 16)),
      );
  }

  return decoded;
}

function asStyleObject(style: TextStyle | undefined): PdfTextStyle {
  if (!style) {
    return {};
  }
  if (Array.isArray(style)) {
    return Object.assign({}, ...style.filter(Boolean)) as PdfTextStyle;
  }
  return style as PdfTextStyle;
}

function mergeTextStyle(
  base: TextStyle | undefined,
  extra: PdfTextStyle,
): PdfTextStyle {
  return { ...asStyleObject(base), ...extra };
}

const UNDERLINE_START = '\uE000';
const UNDERLINE_END = '\uE001';

/** Match web parseMarkdown: Lexical uses __ for underline, not bold. */
export function protectCustomUnderline(markdown: string): string {
  return markdown.replace(/__([\s\S]+?)__/g, (_match, text: string) => {
    return `${UNDERLINE_START}${text}${UNDERLINE_END}`;
  });
}

function renderUnderlineSegments(
  text: string,
  baseStyle: TextStyle,
  keyPrefix: string,
): React.ReactNode[] {
  const decodedText = decodeHtmlEntities(text);
  const parts: React.ReactNode[] = [];
  const pattern = /\uE000([^\uE001]+)\uE001/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let segmentIndex = 0;

  while ((match = pattern.exec(decodedText)) !== null) {
    if (match.index > lastIndex) {
      parts.push(decodedText.slice(lastIndex, match.index));
    }
    parts.push(
      <Text
        key={`${keyPrefix}-ul-${segmentIndex++}`}
        style={mergeTextStyle(baseStyle, { textDecoration: 'underline' })}
      >
        {match[1]}
      </Text>,
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < decodedText.length) {
    parts.push(decodedText.slice(lastIndex));
  }

  if (!parts.length && decodedText) {
    parts.push(decodedText);
  }

  return parts;
}

function renderInlineTokenChildren(
  token: marked.Token,
  baseStyle: TextStyle,
  key: string,
): React.ReactNode {
  switch (token.type) {
    case 'text': {
      const textToken = token as marked.Tokens.Text;
      if (textToken.tokens?.length) {
        return textToken.tokens.map((child, index) =>
          renderInlineTokenChildren(child, baseStyle, `${key}-t-${index}`),
        );
      }
      return renderUnderlineSegments(textToken.text, baseStyle, key);
    }
    case 'strong':
      return (
        <Text key={key} style={mergeTextStyle(baseStyle, { fontWeight: 700 })}>
          {(token.tokens ?? []).map((child, index) =>
            renderInlineTokenChildren(child, baseStyle, `${key}-s-${index}`),
          )}
        </Text>
      );
    case 'em':
      return (
        <Text
          key={key}
          style={mergeTextStyle(baseStyle, { fontStyle: 'italic' })}
        >
          {(token.tokens ?? []).map((child, index) =>
            renderInlineTokenChildren(child, baseStyle, `${key}-e-${index}`),
          )}
        </Text>
      );
    case 'del':
      return (
        <Text
          key={key}
          style={mergeTextStyle(baseStyle, { textDecoration: 'line-through' })}
        >
          {(token.tokens ?? []).map((child, index) =>
            renderInlineTokenChildren(child, baseStyle, `${key}-d-${index}`),
          )}
        </Text>
      );
    case 'link':
      return (
        <Text
          key={key}
          style={mergeTextStyle(baseStyle, { textDecoration: 'underline' })}
        >
          {(
            token.tokens ?? [
              { type: 'text', text: token.text, raw: token.text },
            ]
          ).map((child, index) =>
            renderInlineTokenChildren(
              child as marked.Token,
              baseStyle,
              `${key}-l-${index}`,
            ),
          )}
        </Text>
      );
    case 'codespan':
      return (
        <Text
          key={key}
          style={mergeTextStyle(baseStyle, {
            fontFamily: 'Courier',
            fontSize: 9,
          })}
        >
          {decodeHtmlEntities(token.text)}
        </Text>
      );
    case 'br':
      return '\n';
    default:
      if (
        'tokens' in token &&
        Array.isArray(token.tokens) &&
        token.tokens.length
      ) {
        return token.tokens.map((child, index) =>
          renderInlineTokenChildren(
            child as marked.Token,
            baseStyle,
            `${key}-d-${index}`,
          ),
        );
      }
      if ('text' in token && typeof token.text === 'string') {
        return renderUnderlineSegments(token.text, baseStyle, key);
      }
      return null;
  }
}

function renderInlineTokens(
  tokens: marked.Token[] | undefined,
  baseStyle: TextStyle,
  keyPrefix: string,
  suffix?: React.ReactNode,
): React.ReactNode {
  if (!tokens?.length) {
    return suffix ?? null;
  }

  return (
    <Text style={asStyleObject(baseStyle)}>
      {tokens.map((token, index) =>
        renderInlineTokenChildren(token, baseStyle, `${keyPrefix}-${index}`),
      )}
      {suffix}
    </Text>
  );
}

const LIST_BASE_INDENT = 24;
const LIST_NESTED_INDENT = 20;
const LIST_MARKER_WIDTH = 24;

function getOrderedListMarker(
  index: number,
  depth: number,
  start: number,
): string {
  if (depth === 0) {
    return `${start + index}.`;
  }

  const alphaIndex = index % 26;
  return `${String.fromCharCode(97 + alphaIndex)}.`;
}

function getListMarkerStyle(baseStyle: TextStyle, depth: number): PdfTextStyle {
  return mergeTextStyle(baseStyle, {
    width: LIST_MARKER_WIDTH,
    color: depth === 0 ? colors.neutral[125] : colors.neutral[110],
  });
}

function renderListMarker(
  ordered: boolean,
  index: number,
  depth: number,
  start: number,
  baseStyle: TextStyle,
): React.ReactNode {
  if (ordered) {
    return (
      <Text style={getListMarkerStyle(baseStyle, depth)}>
        {getOrderedListMarker(index, depth, start)}
      </Text>
    );
  }

  if (depth === 0) {
    return <Text style={getListMarkerStyle(baseStyle, depth)}>•</Text>;
  }

  return (
    <View
      style={{
        width: LIST_MARKER_WIDTH,
        alignItems: 'center',
        paddingTop: 4,
      }}
    >
      <View
        style={{
          width: 4,
          height: 4,
          borderRadius: 2,
          borderWidth: 0.75,
          borderColor: colors.neutral[110],
        }}
      />
    </View>
  );
}

function getListPaddingLeft(depth: number): number {
  return LIST_BASE_INDENT + depth * LIST_NESTED_INDENT;
}

const BLOCK_TOKEN_TYPES = new Set([
  'blockquote',
  'code',
  'heading',
  'hr',
  'list',
  'paragraph',
  'table',
]);

function isBlockToken(token: marked.Token): boolean {
  return BLOCK_TOKEN_TYPES.has(token.type);
}

function renderListToken(
  token: marked.Tokens.List,
  baseStyle: TextStyle,
  key: string | number,
  depth = 0,
  suffix?: React.ReactNode,
): React.ReactNode {
  const start = Number(token.start ?? 1);

  return (
    <View
      key={key}
      style={{
        marginBottom: 4,
        paddingLeft: getListPaddingLeft(depth),
      }}
    >
      {token.items.map((item, index) => {
        const isLastItem = index === token.items.length - 1;
        const marker = renderListMarker(
          token.ordered,
          index,
          depth,
          start,
          baseStyle,
        );

        return (
          <View
            key={`${key}-li-${index}`}
            style={{
              flexDirection: 'row',
              marginBottom: 2,
              alignItems: 'flex-start',
            }}
          >
            {marker}
            <View style={{ flex: 1 }}>
              {renderListItemTokens(
                item.tokens,
                baseStyle,
                `li-${key}-${index}`,
                depth,
                suffix && isLastItem ? suffix : undefined,
              )}
            </View>
          </View>
        );
      })}
    </View>
  );
}

function renderListItemTokens(
  tokens: marked.Token[] | undefined,
  baseStyle: TextStyle,
  keyPrefix: string,
  depth: number,
  suffix?: React.ReactNode,
): React.ReactNode {
  if (!tokens?.length) {
    return suffix ?? null;
  }

  const parts: React.ReactNode[] = [];
  let inlineBatch: marked.Token[] = [];

  const flushInline = (key: string) => {
    if (!inlineBatch.length) {
      return;
    }

    parts.push(renderInlineTokens(inlineBatch, baseStyle, key));
    inlineBatch = [];
  };

  tokens.forEach((token, index) => {
    if (token.type === 'list') {
      flushInline(`${keyPrefix}-inline-${index}`);
      parts.push(
        renderListToken(
          token as marked.Tokens.List,
          baseStyle,
          `${keyPrefix}-nested-${index}`,
          depth + 1,
        ),
      );
      return;
    }

    if (token.type === 'paragraph') {
      flushInline(`${keyPrefix}-inline-${index}`);
      parts.push(
        <View key={`${keyPrefix}-p-${index}`} style={{ marginBottom: 2 }}>
          {renderInlineTokens(
            (token as marked.Tokens.Paragraph).tokens,
            baseStyle,
            `${keyPrefix}-p-${index}`,
          )}
        </View>,
      );
      return;
    }

    if (isBlockToken(token)) {
      flushInline(`${keyPrefix}-inline-${index}`);
      parts.push(
        renderBlockToken(token, baseStyle, `${keyPrefix}-block-${index}`),
      );
      return;
    }

    inlineBatch.push(token);
  });

  flushInline(`${keyPrefix}-inline-final`);

  if (!parts.length) {
    return suffix ?? null;
  }

  return (
    <View>
      {parts}
      {suffix}
    </View>
  );
}

function renderBlockToken(
  token: marked.Token,
  baseStyle: TextStyle,
  key: React.Key,
  suffix?: React.ReactNode,
): React.ReactNode {
  switch (token.type) {
    case 'paragraph':
      return (
        <View key={key} style={{ marginBottom: 4 }}>
          {renderInlineTokens(token.tokens, baseStyle, `p-${key}`, suffix)}
        </View>
      );
    case 'heading': {
      const headingSizes: Record<number, number> = {
        1: 14,
        2: 13,
        3: 12,
        4: 11,
        5: 10,
        6: 10,
      };
      const fontSize = headingSizes[token.depth] ?? 11;
      return (
        <View key={key} style={{ marginBottom: 4 }}>
          {renderInlineTokens(
            token.tokens,
            mergeTextStyle(baseStyle, { fontSize }),
            `h-${key}`,
          )}
        </View>
      );
    }
    case 'list':
      return renderListToken(
        token as marked.Tokens.List,
        baseStyle,
        key,
        0,
        suffix,
      );
    case 'blockquote':
      return (
        <View
          key={key}
          style={{
            marginBottom: 4,
            paddingLeft: 8,
            borderLeftWidth: 2,
            borderLeftColor: '#E5E5E5',
          }}
        >
          {token.tokens.map((child, index) =>
            renderBlockToken(child, baseStyle, `${key}-bq-${index}`),
          )}
        </View>
      );
    case 'code':
      return (
        <View key={key} style={{ marginBottom: 4 }}>
          <Text
            style={mergeTextStyle(baseStyle, {
              fontFamily: 'Courier',
              fontSize: 9,
            })}
          >
            {decodeHtmlEntities(token.text)}
          </Text>
        </View>
      );
    case 'hr':
      return (
        <View
          key={key}
          style={{
            borderBottomWidth: 1,
            borderBottomColor: '#E5E5E5',
            marginVertical: 6,
          }}
        />
      );
    case 'space':
      return null;
    default:
      if ('tokens' in token && Array.isArray(token.tokens)) {
        return (
          <View key={key} style={{ marginBottom: 4 }}>
            {renderInlineTokens(token.tokens, baseStyle, `d-${key}`)}
          </View>
        );
      }
      if ('text' in token && typeof token.text === 'string') {
        return (
          <View key={key} style={{ marginBottom: 4 }}>
            {renderInlineTokens(
              [{ type: 'text', text: token.text, raw: token.text }],
              baseStyle,
              `t-${key}`,
            )}
          </View>
        );
      }
      return null;
  }
}

export function renderMarkdownForPdf(
  markdown: string | null | undefined,
  baseStyle: TextStyle,
  suffix?: React.ReactNode,
): React.ReactNode {
  if (!markdown?.trim()) {
    return suffix ? (
      <Text style={asStyleObject(baseStyle)}>{suffix}</Text>
    ) : null;
  }

  const normalized = decodeHtmlEntities(markdown.trim());
  const tokens = marked.lexer(
    protectCustomUnderline(normalized),
    getMarkedDisplayOptions(),
  );

  if (!tokens.length) {
    return (
      <Text style={asStyleObject(baseStyle)}>
        {normalized}
        {suffix}
      </Text>
    );
  }

  return tokens.map((token, index) =>
    renderBlockToken(
      token,
      baseStyle,
      index,
      index === tokens.length - 1 ? suffix : undefined,
    ),
  );
}

const PdfFormattedText: React.FC<{
  children: string | null | undefined;
  style?: TextStyle;
  suffix?: React.ReactNode;
}> = ({ children, style, suffix }) => {
  const content = renderMarkdownForPdf(children, style ?? {}, suffix);

  if (!content) {
    return null;
  }

  return <View>{content}</View>;
};

export default PdfFormattedText;
